import React, { useEffect, useState } from "react";
import { useRole } from "../context/RoleContext";
import { getAdminAudit, adminExportUrl } from "../lib/adminPortal";
import { Download, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const COLLECTIONS = [
  { v: "all", label: "All" },
  { v: "users", label: "Users" },
  { v: "architect_applications", label: "Applications" },
  { v: "designs", label: "Designs" },
  { v: "projects", label: "Projects" },
  { v: "consultations", label: "Consultations" },
  { v: "code_changes", label: "Code Changes" },
  { v: "documents", label: "Documents" },
  { v: "architects", label: "Architects" },
];

const ACTOR_ROLES = [
  { v: "all", label: "All actors" },
  { v: "city_staff", label: "City Staff" },
  { v: "architect", label: "Architect" },
  { v: "owner_builder", label: "Owner / Builder" },
  { v: "platform_admin", label: "Platform Admin" },
  { v: "system", label: "System" },
];

export default function AdminAudit() {
  const { user } = useRole();
  const [collection, setCollection] = useState("all");
  const [types, setTypes] = useState([]);
  const [actor, setActor] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getAdminAudit(user.id, { collection, types, actor, from, to }).then(
      (d) => alive && setData(d),
    );
    return () => {
      alive = false;
    };
  }, [user?.id, collection, types, actor, from, to]);

  const csvParams = {
    collection,
    types: types.join(","),
    actor,
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  return (
    <section className="animate-fade-in-up" data-testid="admin-audit-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>Platform Admin · Audit</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Platform Audit.
          </h1>
          <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 4 }}>
            Every event across every collection. Defaults to all events; filter to focus.
          </p>
        </div>
        <a
          href={adminExportUrl("audit", user?.id, csvParams)}
          className="wn-btn wn-btn-tertiary"
          data-testid="admin-audit-export"
        >
          <Download size={14} /> Export to CSV
        </a>
      </div>

      <div className="mt-3" data-testid="admin-audit-email-link-row">
        <Link
          to="/admin/email-log"
          style={{ color: "#1B1F4B", fontSize: 12.5, textDecoration: "underline", textUnderlineOffset: 3 }}
          data-testid="admin-audit-email-log-link"
        >
          <Mail size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 5 }} />
          View the simulated email log →
        </Link>
      </div>

      <div className="mt-6 wn-card" style={{ padding: 16 }} data-testid="admin-audit-filters">
        <div className="wn-label">Collection</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {COLLECTIONS.map((c) => {
            const active = collection === c.v;
            return (
              <button
                key={c.v}
                onClick={() => setCollection(c.v)}
                data-testid={`admin-audit-coll-${c.v}`}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                  background: active ? "#1B1F4B" : "transparent",
                  color: active ? "#FBFAFD" : "#272636",
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <div>
            <label className="wn-label block">Actor</label>
            <select
              className="wn-input"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              data-testid="admin-audit-actor"
            >
              {ACTOR_ROLES.map((a) => (
                <option key={a.v} value={a.v}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="wn-label block">From</label>
            <input
              type="date"
              className="wn-input tabular-nums"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="admin-audit-from"
            />
          </div>
          <div>
            <label className="wn-label block">To</label>
            <input
              type="date"
              className="wn-input tabular-nums"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="admin-audit-to"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 wn-card" style={{ padding: 0, overflow: "hidden" }} data-testid="admin-audit-table-card">
        <table className="w-full" style={{ borderCollapse: "collapse" }} data-testid="admin-audit-table">
          <thead>
            <tr style={{ background: "#1B1F4B", color: "#FBFAFD" }}>
              <th style={th}>Timestamp</th>
              <th style={th}>Actor</th>
              <th style={th}>Action</th>
              <th style={th}>Collection</th>
              <th style={th}>Record</th>
              <th style={th}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {!data && (
              <tr><td colSpan={6} style={{ padding: 24, color: "#6C6B85", fontStyle: "italic", textAlign: "center" }}>Loading events…</td></tr>
            )}
            {data && data.items.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, color: "#6C6B85", textAlign: "center" }} data-testid="admin-audit-empty">
                No events match these filters.
              </td></tr>
            )}
            {data?.items.map((a, i) => (
              <tr
                key={a.id || i}
                style={{ background: i % 2 === 0 ? "#FBFAFD" : "#F8F7FB" }}
                data-testid={`admin-audit-row-${a.id || i}`}
              >
                <td style={{ ...td, fontWeight: 600 }} className="tabular-nums">{formatTs(a.at)}</td>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: "#1B1F4B" }}>
                    {a.actor?.name || a.actor_id || "—"}
                  </div>
                  {a.actor?.role && (
                    <div className="wn-label" style={{ fontSize: 10 }}>{a.actor.role.replace(/_/g, " ")}</div>
                  )}
                </td>
                <td style={{ ...td, fontWeight: 600 }}>{prettyAction(a.action)}</td>
                <td style={td}>{a.record_type || "—"}</td>
                <td style={{ ...td, color: "#3A5563" }} className="tabular-nums">{a.record_id}</td>
                <td style={{ ...td, color: "#272636" }} title={a.note}>
                  <div style={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {a.note || "—"}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const th = { textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" };
const td = { padding: "10px 14px", fontSize: 12.5, color: "#272636", borderTop: "1px solid #D9D8E4", verticalAlign: "top" };

function formatTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 19); } catch { return iso; }
}
function prettyAction(a) {
  if (!a) return "—";
  return a.replace(/_/g, " ").replace(/\./g, " → ").replace(/\b\w/g, (c) => c.toUpperCase());
}
